import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import FiscalFechamentoCliente from '../components/FiscalFechamentoCliente';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalModerno.css';

// Fiscal → Fechamento Fiscal Mensal (port do ZADRA). O operador escolhe a empresa e a
// competência; o Saber lê as NFS-e nacionais já baixadas, aplica os eventos (cancelamento,
// substituição...), confere as retenções e gera o XLSX (Resumo, Emitidas, Recebidas).
// O histórico inclui os fechamentos feitos no ZADRA (esses ficaram no Drive).
// O e-mail de conclusão aqui é só PRÉVIA: esta tela não envia nada.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

const fmtMoeda = (v) => (v == null || v === '' ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const fmtComp = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');
const fmtCnpj = (c) => (c && c.length === 14 ? c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : c || '—');
const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function mesAnterior() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function seloStatus(f) {
  if (!f) return null;
  if (f.status === 'Concluído') {
    return f.qtd_avisos > 0
      ? { cls: 'alerta', ico: 'fa-triangle-exclamation', txt: `Concluído com ${f.qtd_avisos} aviso(s)` }
      : { cls: 'ok', ico: 'fa-circle-check', txt: 'Concluído' };
  }
  if (f.status === 'Erro') return { cls: 'erro', ico: 'fa-circle-xmark', txt: 'Erro' };
  return { cls: 'neutro', ico: 'fa-clock', txt: f.status || '—' };
}

function Selo({ f }) {
  const s = seloStatus(f);
  return s ? <span className={`fz-selo ${s.cls}`}><i className={`fas ${s.ico}`}></i>{s.txt}</span> : null;
}

function TabelaTotais({ resumo }) {
  const e = resumo.emitidas || {};
  const r = resumo.recebidas || {};
  const linhas = [
    ['Notas ativas', e.ativas, r.ativas, false],
    ['Canceladas', e.canceladas, r.canceladas, false],
    ['Valor bruto', e.bruto, r.bruto, true],
    ['Valor líquido', e.liquido, r.liquido, true],
    ['ISSQN', e.issqn, null, true],
    ['ISS retido', e.retencoes?.iss, r.retencoes?.iss, true],
    ['IRRF', e.retencoes?.irrf, r.retencoes?.irrf, true],
    ['CSRF', e.retencoes?.csrf, r.retencoes?.csrf, true],
    ['INSS', e.retencoes?.inss, r.retencoes?.inss, true],
  ];
  return (
    <table className="table table-sm po-table align-middle mb-0">
      <thead><tr><th></th><th className="text-end">Emitidas</th><th className="text-end">Recebidas</th></tr></thead>
      <tbody>
        {linhas.map(([rot, ve, vr, dinheiro]) => (
          <tr key={rot}>
            <td className="fw-semibold">{rot}</td>
            <td className="text-end fz-num">{dinheiro ? fmtMoeda(ve) : (ve ?? '—')}</td>
            <td className="text-end fz-num">{vr == null ? '—' : (dinheiro ? fmtMoeda(vr) : vr)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function FiscalFechamento() {
  const [empresas, setEmpresas] = useState([]);
  const [buscaEmpresa, setBuscaEmpresa] = useState('');
  const [empresaCod, setEmpresaCod] = useState('');
  const [competencia, setCompetencia] = useState(mesAnterior());
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [atual, setAtual] = useState(null);             // fechamento com resumo
  const [tipoAviso, setTipoAviso] = useState('');
  const [verEventos, setVerEventos] = useState(false);
  const [historico, setHistorico] = useState(null);
  const [verHistorico, setVerHistorico] = useState(false);   // histórico em modal, aberto pelo botão do topo
  const [andamento, setAndamento] = useState([]);
  const [carregandoHist, setCarregandoHist] = useState(false);
  const [filtros, setFiltros] = useState({ busca: '', competencia: '', status: '', origem: '' });
  const [previa, setPrevia] = useState(null);           // { carregando, dados, fechamento }
  const [baixando, setBaixando] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/fiscal/empresas/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => setEmpresas((r.data.empresas || []).filter((e) => e.ativa)))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar as empresas'));
  }, []);

  const carregarHistorico = useCallback(() => {
    setCarregandoHist(true);
    const params = { _: Date.now() };
    Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
    axios.get(`${API}/api/fiscal/fechamento/`, { params, headers: headers() })
      .then((r) => { setHistorico(r.data.fechamentos); setAndamento(r.data.em_andamento || []); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar o histórico'))
      .finally(() => setCarregandoHist(false));
  }, [filtros]);
  useEffect(() => { carregarHistorico(); }, [carregarHistorico]);

  const empresasFiltradas = useMemo(() => {
    const termo = semAcento(buscaEmpresa.trim());
    if (!termo) return empresas;
    return empresas.filter((e) => semAcento(e.cliente).includes(termo) || String(e.cod) === termo
      || (e.cnpj || '').includes(buscaEmpresa.replace(/\D/g, '') || '#'));
  }, [empresas, buscaEmpresa]);

  const empresaEscolhida = empresas.find((e) => String(e.cod) === String(empresaCod));

  const gerar = () => {
    setGerando(true); setErro(''); setMsg(''); setAtual(null); setTipoAviso('');
    axios.post(`${API}/api/fiscal/fechamento/gerar/`, { empresa_cod: Number(empresaCod), competencia }, { headers: headers() })
      .then((r) => {
        setAtual(r.data.fechamento);
        if (r.data.success) setMsg(r.data.mensagem);
        else if (r.data.ordem?.status === 'pendente' || r.data.ordem?.status === 'processando') setMsg(r.data.mensagem);
        else setErro(r.data.mensagem || 'O fechamento não pôde ser concluído');
        carregarHistorico();
      })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível gerar o fechamento'))
      .finally(() => setGerando(false));
  };

  const abrir = (id) => {
    setErro(''); setTipoAviso(''); setVerEventos(false);
    axios.get(`${API}/api/fiscal/fechamento/${id}/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => { setAtual(r.data.fechamento); window.scrollTo({ top: 0, behavior: 'smooth' }); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível abrir o fechamento'));
  };

  const baixar = (f) => {
    setBaixando(f.id); setErro('');
    axios.get(`${API}/api/fiscal/fechamento/${f.id}/baixar/`, { headers: headers(), responseType: 'blob' })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = f.arquivo || 'fechamento.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      })
      .catch(() => setErro('Não foi possível baixar o XLSX'))
      .finally(() => setBaixando(''));
  };

  const abrirPrevia = (f) => {
    setPrevia({ carregando: true, dados: null, fechamento: f });
    axios.get(`${API}/api/fiscal/fechamento/${f.id}/previa-email/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => setPrevia({ carregando: false, dados: r.data.previa, fechamento: f }))
      .catch((e) => { setPrevia(null); setErro(e.response?.data?.mensagem || 'Não foi possível montar a prévia'); });
  };

  const resumo = atual?.resumo;
  const avisos = useMemo(() => (resumo?.avisos || []).filter((a) => !tipoAviso || a.tipo === tipoAviso), [resumo, tipoAviso]);
  const setFiltro = (k, v) => setFiltros((f) => ({ ...f, [k]: v }));

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-calendar-check header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Fechamento Fiscal Mensal</h2>
          </div>
          <div className="gr-toolbar">
            <button type="button" className="gr-btn" onClick={() => { setVerHistorico(true); carregarHistorico(); }}>
              <i className="fas fa-clock-rotate-left" aria-hidden="true"></i>Histórico
            </button>
            <button type="button" className="gr-btn" onClick={carregarHistorico} disabled={carregandoHist}>
              <i className={`fas ${carregandoHist ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
          </div>
        </div>

        <div className="alert alert-light border small py-2">
          <i className="fas fa-circle-info me-2 text-primary"></i>
          Usa as NFS-e do <b>Portal Nacional</b> já baixadas para a empresa (São Paulo não entra). Canceladas e substituídas ficam
          no detalhe, fora dos totais. Evento com código desconhecido numa nota da competência <b>bloqueia</b> o fechamento.
          Cada geração cria um arquivo novo; os anteriores são mantidos.
        </div>
        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" role="button" onClick={() => setMsg('')}><i className="fas fa-circle-check me-2"></i>{msg}</div>}

        <section className="po-card po-enter mb-3">
          <header className="po-card-head"><h3><i className="fas fa-play" aria-hidden="true"></i>Gerar fechamento</h3></header>
          <div className="po-card-body">
            <div className="row g-3 align-items-end">
              <div className="col-lg-3 col-md-6">
                <label className="form-label small fw-bold mb-1">Buscar empresa</label>
                <input className="form-control" placeholder="Código, nome ou CNPJ" value={buscaEmpresa}
                  onChange={(e) => {
                    // como no Monitor: código exato, CNPJ completo ou um único nome já seleciona a empresa
                    const valor = e.target.value;
                    setBuscaEmpresa(valor);
                    const t = valor.trim();
                    const dig = t.replace(/\D/g, '');
                    const termo = semAcento(t);
                    const porNome = termo ? empresas.filter((x) => semAcento(x.cliente).includes(termo)) : [];
                    const achada = empresas.find((x) => String(x.cod) === t)
                      || (dig.length === 14 ? empresas.find((x) => (x.cnpj || '') === dig) : null)
                      || (porNome.length === 1 ? porNome[0] : null);
                    if (achada) setEmpresaCod(String(achada.cod));
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && empresasFiltradas.length) setEmpresaCod(String(empresasFiltradas[0].cod)); }} />
              </div>
              <div className="col-lg-4 col-md-6">
                <label className="form-label small fw-bold mb-1">Empresa ({empresasFiltradas.length} ativa(s))</label>
                <select className="form-select" value={empresaCod} onChange={(e) => setEmpresaCod(e.target.value)}>
                  <option value="">Selecione…</option>
                  {empresasFiltradas.map((e) => <option key={e.cod} value={e.cod}>{e.cod} · {e.cliente}</option>)}
                </select>
              </div>
              <div className="col-lg-2 col-md-6">
                <label className="form-label small fw-bold mb-1">Competência</label>
                <input type="month" className="form-control" value={competencia} onChange={(e) => setCompetencia(e.target.value)} />
              </div>
              <div className="col-lg-3 col-md-6">
                <button className="btn btn-primary w-100" onClick={gerar} disabled={gerando || !empresaCod || !competencia}>
                  <i className={`fas ${gerando ? 'fa-spinner fa-spin' : 'fa-file-excel'} me-2`}></i>{gerando ? 'Gerando…' : 'Gerar fechamento'}
                </button>
              </div>
            </div>
            {empresaEscolhida && <div className="fz-sub fz-mono mt-2">CNPJ {fmtCnpj(empresaEscolhida.cnpj)} · {empresaEscolhida.tributacao || 'tributação não informada'}</div>}
            {gerando && (
              <div className="text-muted small mt-3"><i className="fas fa-spinner fa-spin me-2" style={{ color: '#00b5cc' }}></i>
                Lendo os XMLs, aplicando eventos e montando o XLSX. Pode levar alguns minutos — não feche a página.
              </div>
            )}
          </div>
        </section>

        {empresaCod && competencia && (
          <FiscalFechamentoCliente empresaCod={Number(empresaCod)} competencia={competencia} setErro={setErro} setMsg={setMsg} />
        )}

        {atual && (
          <section className="po-card po-enter mb-3">
            <header className="po-card-head">
              <h3><i className="fas fa-clipboard-check" aria-hidden="true"></i>{atual.cliente} · {fmtComp(atual.competencia)}</h3>
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <Selo f={atual} />
                {atual.tem_arquivo && (
                  <button className="btn btn-sm btn-outline-primary" onClick={() => baixar(atual)} disabled={baixando === atual.id}>
                    <i className={`fas ${baixando === atual.id ? 'fa-spinner fa-spin' : 'fa-download'} me-1`}></i>Baixar XLSX
                  </button>
                )}
                <button className="btn btn-sm btn-outline-secondary" onClick={() => abrirPrevia(atual)}>
                  <i className="fas fa-envelope-open-text me-1"></i>Prévia do e-mail
                </button>
                <button type="button" className="btn-close ms-1" aria-label="Fechar" onClick={() => setAtual(null)}></button>
              </div>
            </header>
            <div className="po-card-body">
              {atual.mensagem && <p className="mb-2">{atual.mensagem}</p>}
              <div className="fz-sub mb-3">
                {atual.concluido_em && <>Gerado em {atual.concluido_em}</>}
                {atual.solicitado_por && <> · por {atual.solicitado_por}</>}
                {atual.arquivo && <> · <span className="fz-mono">{atual.arquivo}</span></>}
                {atual.observacao_arquivo && <> · {atual.observacao_arquivo}</>}
              </div>
              {!resumo ? (
                <div className="text-muted small">Sem resumo detalhado {atual.origem === 'zadra' ? '(fechamento gerado no ZADRA).' : '.'}</div>
              ) : (
                <>
                  <div className="po-kpis" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                    <div className="po-kpi c-navy"><div className="po-kpi-rotulo"><i className="fas fa-file-export"></i>Emitidas ativas</div><div className="po-kpi-valor">{resumo.emitidas?.ativas ?? 0}</div></div>
                    <div className="po-kpi c-navy"><div className="po-kpi-rotulo"><i className="fas fa-file-import"></i>Recebidas ativas</div><div className="po-kpi-valor">{resumo.recebidas?.ativas ?? 0}</div></div>
                    <div className={`po-kpi c-bad ${resumo.canceladas ? '' : 'po-zero'}`}><div className="po-kpi-rotulo"><i className="fas fa-ban"></i>Canceladas</div><div className="po-kpi-valor">{resumo.canceladas || 0}</div></div>
                    <div className={`po-kpi c-warn ${resumo.substituidas ? '' : 'po-zero'}`}><div className="po-kpi-rotulo"><i className="fas fa-right-left"></i>Substituídas</div><div className="po-kpi-valor">{resumo.substituidas || 0}</div></div>
                  </div>
                  <div className="row g-3">
                    <div className="col-lg-6"><TabelaTotais resumo={resumo} /></div>
                    <div className="col-lg-6">
                      <div className="fw-bold small mb-1">Emitidas por código de serviço (LC 116)</div>
                      <table className="table table-sm po-table align-middle mb-3">
                        <thead><tr><th>Código</th><th>Descrição</th><th className="text-end">Qtde</th><th className="text-end">Bruto</th></tr></thead>
                        <tbody>
                          {(resumo.por_codigo || []).map((g) => (
                            <tr key={g.codigo || 'sem'}>
                              <td className="fz-mono">{g.codigo || '—'}</td>
                              <td className="small">{g.descricao || <span className="text-muted">Código sem descrição na tabela LC 116</span>}</td>
                              <td className="text-end fz-num">{g.quantidade}</td>
                              <td className="text-end fz-num">{fmtMoeda(g.valor_bruto)}</td>
                            </tr>
                          ))}
                          {!(resumo.por_codigo || []).length && <tr><td colSpan={4} className="text-muted text-center">Sem emitidas ativas.</td></tr>}
                        </tbody>
                      </table>
                      {(resumo.por_simples || []).length > 0 && (
                        <>
                          <div className="fw-bold small mb-1">Recebidas por regime do prestador</div>
                          <table className="table table-sm po-table align-middle mb-0">
                            <tbody>
                              {resumo.por_simples.map((g) => (
                                <tr key={g.categoria}><td>{g.categoria}</td><td className="text-end fz-num">{g.quantidade}</td><td className="text-end fz-num">{fmtMoeda(g.valor_bruto)}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      )}
                    </div>
                  </div>

                  {(resumo.eventos_aplicados || []).length > 0 && (
                    <div className="mt-3">
                      <button className="btn btn-sm btn-link p-0" onClick={() => setVerEventos((x) => !x)}>
                        <i className={`fas ${verEventos ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`}></i>
                        {resumo.eventos_aplicados.length} evento(s) aplicado(s)
                      </button>
                      {verEventos && <ul className="small mt-2 mb-0">{resumo.eventos_aplicados.map((t) => <li key={t}>{t}</li>)}</ul>}
                    </div>
                  )}

                  {resumo.qtd_avisos > 0 && (
                    <div className="mt-3">
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                        <span className="fw-bold small"><i className="fas fa-triangle-exclamation text-warning me-1"></i>{resumo.qtd_avisos} aviso(s) para revisão</span>
                        {(resumo.avisos_por_tipo || []).map((t) => (
                          <button key={t.tipo} type="button" className={`btn btn-sm ${tipoAviso === t.tipo ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setTipoAviso((x) => (x === t.tipo ? '' : t.tipo))}>
                            {t.rotulo} <span className="badge bg-light text-dark ms-1">{t.quantidade}</span>
                          </button>
                        ))}
                      </div>
                      <div className="po-scroll" style={{ maxHeight: 280 }}>
                        <ul className="small mb-0">{avisos.map((a, i) => <li key={`${i}-${a.texto.slice(0, 20)}`} className="mb-1" style={{ overflowWrap: 'anywhere' }}>{a.texto}</li>)}</ul>
                      </div>
                      {resumo.avisos_truncados && <div className="fz-sub mt-1">Lista resumida: a íntegra está na coluna Observacoes do XLSX.</div>}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        )}

        {verHistorico && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-clock-rotate-left me-2" style={{ color: '#00b5cc' }}></i>Histórico de fechamentos</h5>
                    <small style={{ color: '#6c757d' }}>{historico ? `${historico.length} fechamento(s)` : 'Carregando…'} — XLSX gerados no Saber e vindos do ZADRA</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setVerHistorico(false)}></button>
                </div>
                <div className="modal-body">
            <div className="fz-filtros">
              <input className="po-busca" placeholder="Código, nome ou CNPJ" value={filtros.busca} onChange={(e) => setFiltro('busca', e.target.value)} />
              <input type="month" className="fz-sel" value={filtros.competencia} onChange={(e) => setFiltro('competencia', e.target.value)} title="Competência" />
              <select className="fz-sel" value={filtros.status} onChange={(e) => setFiltro('status', e.target.value)}>
                <option value="">Todos os status</option><option>Concluído</option><option>Erro</option><option>Pendente</option>
              </select>
              <select className="fz-sel" value={filtros.origem} onChange={(e) => setFiltro('origem', e.target.value)}>
                <option value="">Saber e ZADRA</option><option value="saber">Gerados no Saber</option><option value="zadra">Vindos do ZADRA</option>
              </select>
            </div>
            {andamento.length > 0 && (
              <div className="alert alert-info py-2 small">
                <i className="fas fa-hourglass-half me-2"></i>Na fila: {andamento.map((o) => `${o.cliente || o.empresa_cod} (${fmtComp(o.competencia)}${o.progresso ? ` — ${o.progresso}` : ''})`).join('; ')}
              </div>
            )}
            <div className="po-scroll" style={{ maxHeight: 'none', overflow: 'visible' }}>
              <table className="table table-hover po-table align-middle mb-0">
                <thead><tr><th>Empresa</th><th>Competência</th><th>Status</th><th>Gerado em</th><th>Solicitado por</th><th>Arquivo</th><th></th></tr></thead>
                <tbody>
                  {(historico || []).map((f) => (
                    <tr key={f.id}>
                      <td><div className="fw-semibold">{f.cliente}</div><div className="fz-sub fz-mono">{f.empresa_cod}{f.origem === 'zadra' ? ' · ZADRA' : ''}</div></td>
                      <td className="fz-num">{fmtComp(f.competencia)}</td>
                      <td><Selo f={f} /></td>
                      <td className="small fz-num">{f.concluido_em || '—'}</td>
                      <td className="small">{f.solicitado_por || '—'}</td>
                      <td className="small" style={{ maxWidth: 280, overflowWrap: 'anywhere' }}>
                        {f.arquivo ? <span className="fz-mono">{f.arquivo}</span> : <span className="text-muted">—</span>}
                        {f.observacao_arquivo && <div className="fz-sub">{f.observacao_arquivo}</div>}
                      </td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-sm btn-link" title="Ver resumo" onClick={() => { setVerHistorico(false); abrir(f.id); }}><i className="fas fa-eye"></i></button>
                        {f.tem_arquivo && (
                          <button className="btn btn-sm btn-link" title="Baixar XLSX" onClick={() => baixar(f)} disabled={baixando === f.id}>
                            <i className={`fas ${baixando === f.id ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                          </button>
                        )}
                        <button className="btn btn-sm btn-link" title="Prévia do e-mail" onClick={() => abrirPrevia(f)}><i className="fas fa-envelope-open-text"></i></button>
                      </td>
                    </tr>
                  ))}
                  {historico && historico.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">Nenhum fechamento com esses filtros.</td></tr>}
                  {!historico && <tr><td colSpan={7} className="text-center text-muted py-4"><i className="fas fa-spinner fa-spin me-2"></i>Carregando…</td></tr>}
                </tbody>
              </table>
            </div>
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary" onClick={() => setVerHistorico(false)}><i className="fas fa-times me-2"></i>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {previa && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-envelope-open-text me-2" style={{ color: '#00b5cc' }}></i>Prévia do e-mail de conclusão</h5>
                    <small style={{ color: '#6c757d' }}>{previa.fechamento.cliente} · {fmtComp(previa.fechamento.competencia)} — somente visualização, nada é enviado</small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setPrevia(null)}></button>
                </div>
                <div className="modal-body">
                  {previa.carregando ? (
                    <div className="text-center text-muted py-4"><i className="fas fa-spinner fa-spin me-2"></i>Montando a prévia…</div>
                  ) : (
                    <>
                      <dl className="row small mb-2">
                        <dt className="col-sm-2">De</dt><dd className="col-sm-10">{previa.dados.de}</dd>
                        <dt className="col-sm-2">Para</dt><dd className="col-sm-10">{previa.dados.para || <span className="text-danger">sem destinatário</span>}</dd>
                        <dt className="col-sm-2">Assunto</dt><dd className="col-sm-10">{previa.dados.assunto}</dd>
                        <dt className="col-sm-2">Anexo</dt>
                        <dd className="col-sm-10">
                          {previa.dados.anexo
                            ? <><i className="fas fa-paperclip me-1"></i><span className="fz-mono">{previa.dados.anexo.nome}</span>{!previa.dados.anexo.disponivel && <span className="text-muted"> (no Drive do ZADRA)</span>}</>
                            : <span className="text-muted">nenhum</span>}
                        </dd>
                      </dl>
                      {previa.dados.aviso && <div className="alert alert-warning py-2 small"><i className="fas fa-triangle-exclamation me-2"></i>{previa.dados.aviso}</div>}
                      {!previa.dados.remetente_autenticado && <div className="alert alert-light border py-2 small"><i className="fas fa-circle-info me-2"></i>A caixa fiscal@ ainda não está autenticada em Fiscal → Configurações.</div>}
                      <iframe title="Prévia do e-mail" sandbox="" srcDoc={previa.dados.html} style={{ width: '100%', height: 300, border: '1px solid #e9ecef', borderRadius: 6, background: '#fff' }} />
                    </>
                  )}
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary" onClick={() => setPrevia(null)}><i className="fas fa-times me-2"></i>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
